import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Button,
  Snackbar,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Hotel as HotelIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalOffer as DiscountIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  NightsStay as NightIcon,
  FlashOn as FlashIcon,
  Star as StarIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

// Import API handler
import { getAllDiscounts, getHotelAndHost } from '../api/adminPanelApi';

// Format currency for display
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount);
};

// Format date for display
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Get discount type icon and color
const getDiscountTypeInfo = (type) => {
  switch (type) {
    case 'tabtrips_promotion':
      return {
        icon: <StarIcon sx={{ fontSize: 16 }} />,
        color: '#FF7700',
        label: 'TabTrips Promotion',
        bgColor: '#fff3e0'
      };
    case 'last_minute_deal':
      return {
        icon: <FlashIcon sx={{ fontSize: 16 }} />,
        color: '#f44336',
        label: 'Last Minute Deal',
        bgColor: '#ffebee'
      };
    case 'night_owl_sale':
      return {
        icon: <NightIcon sx={{ fontSize: 16 }} />,
        color: '#9c27b0',
        label: 'Night Owl Sale',
        bgColor: '#f3e5f5'
      };
    case 'long_stay':
      return {
        icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
        color: '#4caf50',
        label: 'Long Stay',
        bgColor: '#e8f5e8'
      };
    default:
      return {
        icon: <DiscountIcon sx={{ fontSize: 16 }} />,
        color: '#2196f3',
        label: 'Special Offer',
        bgColor: '#e3f2fd'
      };
  }
};

const DiscountManagement = () => {
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch discounts and hotels data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [discountsResult, hotelsResult] = await Promise.all([
        getAllDiscounts(),
        getHotelAndHost()
      ]);

      if (discountsResult.success && hotelsResult.success) {
        setDiscounts(discountsResult.data.discounts || []);
        setHotels(hotelsResult.data.data || []);
        setSnackbar({
          open: true,
          message: `Loaded ${discountsResult.data.discounts?.length || 0} discounts for ${hotelsResult.data.data?.length || 0} hotels`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: discountsResult.error || hotelsResult.error || 'Failed to fetch data',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch discount data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get hotel name by ID
  const getHotelName = (hotelId) => {
    const hotel = hotels.find(h => h.hotel_id === hotelId);
    return hotel ? hotel.hotel_name : `Hotel ${hotelId}`;
  };

  // Get hotel info by ID
  const getHotelInfo = (hotelId) => {
    const hotel = hotels.find(h => h.hotel_id === hotelId);
    return hotel || null;
  };

  // Filter discounts based on search and filters
  const filteredDiscounts = discounts.filter(discount => {
    const hotelName = getHotelName(discount.hotel_id).toLowerCase();
    const searchMatch = searchTerm === '' || 
      hotelName.includes(searchTerm.toLowerCase()) ||
      discount.hotel_id.toString().includes(searchTerm) ||
      discount.discount_type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const typeMatch = filterType === 'all' || discount.discount_type === filterType;
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'active' && discount.status === 0) ||
      (filterStatus === 'inactive' && discount.status === 1);
    
    return searchMatch && typeMatch && statusMatch;
  });

  // Group discounts by hotel
  const groupedDiscounts = filteredDiscounts.reduce((acc, discount) => {
    const hotelId = discount.hotel_id;
    if (!acc[hotelId]) {
      acc[hotelId] = [];
    }
    acc[hotelId].push(discount);
    return acc;
  }, {});

  // Get unique discount types for filter
  const discountTypes = [...new Set(discounts.map(d => d.discount_type))];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/')} 
              sx={{ 
                color: '#FF7700',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              Discount Management
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
            sx={{ 
              borderColor: '#FF7700',
              color: '#FF7700',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 119, 0, 0.04)'
              }
            }}
          >
            Refresh Data
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage and view all hotel discounts and promotional offers
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            border: '1px solid #90caf9',
            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HotelIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {hotels.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Hotels</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
            border: '1px solid #ce93d8',
            boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DiscountIcon sx={{ fontSize: 24, color: '#7b1fa2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {discounts.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7b1fa2' }}>Total Discounts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            border: '1px solid #a5d6a7',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {discounts.filter(d => d.status === 0).length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#388e3c' }}>Active Discounts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            border: '1px solid #ffcc80',
            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <StarIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {Object.keys(groupedDiscounts).length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f57c00' }}>Hotels with Discounts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search hotels by name, ID, or discount type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Discount Type</InputLabel>
              <Select
                value={filterType}
                label="Discount Type"
                onChange={(e) => setFilterType(e.target.value)}
                startAdornment={<FilterIcon sx={{ mr: 1, color: '#FF7700' }} />}
              >
                <MenuItem value="all">All Types</MenuItem>
                {discountTypes.map(type => {
                  const typeInfo = getDiscountTypeInfo(type);
                  return (
                    <MenuItem key={type} value={type}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {typeInfo.icon}
                        {typeInfo.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Discount Cards */}
      {Object.keys(groupedDiscounts).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all' 
              ? 'No discounts found matching your search criteria.' 
              : 'No discounts available.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {Object.entries(groupedDiscounts).map(([hotelId, hotelDiscounts]) => {
            const hotelInfo = getHotelInfo(parseInt(hotelId));
            const activeDiscounts = hotelDiscounts.filter(d => d.status === 0);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={hotelId}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}>
                  {/* Hotel Header */}
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ 
                        backgroundColor: '#FF7700',
                        width: 48,
                        height: 48
                      }}>
                        <HotelIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057' }} noWrap>
                          {hotelInfo?.hotel_name || `Hotel ${hotelId}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID: {hotelId}
                        </Typography>
                        {hotelInfo && (
                          <Typography variant="body2" color="text.secondary">
                            {hotelInfo.email} • {hotelInfo.phone}
                          </Typography>
                        )}
                      </Box>
                      <Badge 
                        badgeContent={activeDiscounts.length} 
                        color="success"
                        sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
                      >
                        <Chip 
                          label={`${hotelDiscounts.length} Discount${hotelDiscounts.length !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{ backgroundColor: '#FF7700', color: 'white' }}
                        />
                      </Badge>
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Discounts List */}
                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                      {hotelDiscounts.map((discount, index) => {
                        const typeInfo = getDiscountTypeInfo(discount.discount_type);
                        const isActive = discount.status === 0;
                        
                        return (
                          <Paper 
                            key={discount.id}
                            sx={{ 
                              p: 2, 
                              mb: index < hotelDiscounts.length - 1 ? 2 : 0,
                              backgroundColor: isActive ? typeInfo.bgColor : '#f5f5f5',
                              border: `1px solid ${isActive ? typeInfo.color : '#e0e0e0'}`,
                              opacity: isActive ? 1 : 0.7
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ color: typeInfo.color }}>
                                  {typeInfo.icon}
                                </Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: typeInfo.color }}>
                                  {typeInfo.label}
                                </Typography>
                              </Box>
                              <Chip 
                                label={isActive ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{ 
                                  backgroundColor: isActive ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontSize: '0.7rem'
                                }}
                              />
                            </Box>
                            
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#2E4057', mb: 1 }}>
                              {discount.min_percentage}%
                              {discount.max_percentage && ` - ${discount.max_percentage}%`}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(discount.start_date)} - {formatDate(discount.end_date)}
                                </Typography>
                              </Box>
                              
                              {discount.min_days && discount.max_days && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {discount.min_days} - {discount.max_days} days
                                  </Typography>
                                </Box>
                              )}
                              
                              {discount.start_time && discount.end_time && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {discount.start_time} - {discount.end_time}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Created: {formatDate(discount.insert_time)}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DiscountManagement;















